/**
 * NeutralHostRoutes — the complete route table of the tenant-neutral host
 * (app.systemdox.com; localhost without ?tenant in dev).
 *
 * Contract (specs/archive/tenant-url-structure, #553): the neutral host
 * serves authentication and hand-off only — login, signup, /join, MFA
 * setup, the one-time welcome — never tenant content. Every other path
 * funnels through the TenantNeutralGate, which resolves the user's
 * workspace and forwards the FULL url (path, query and hash) to its
 * tenant root host ({slug}.systemdox.com).
 *
 * Two rules keep this table honest (neutralHostRoutes.test.ts pins both):
 *
 * 1. /settings/github/callback is served HERE, before anything can detour
 *    it. GitHub Apps redirect post-install to one fixed callback URL —
 *    this host — carrying a single-use, short-lived OAuth ?code. Nothing
 *    that might navigate away (the MFA gate, the welcome step, workspace
 *    resolution) may run ahead of redeeming it: the page POSTs straight
 *    to the API, which resolves the tenant from the token — valid on
 *    every subdomain via the .systemdox.com cookie session — and only
 *    then navigates on. Until #885 this path fell into the catch-all,
 *    whose /launch rewrite silently dropped the query string, so GitHub
 *    installed the App but SystemDox never heard about it.
 *
 * 2. The catch-all must not rewrite the URL before the gate has seen it.
 *    <Navigate> drops the query string and hash, so it may only run as
 *    the gate's pass-through fallback (pending invite, welcome due, no
 *    workspace to forward to) — never ahead of the gate.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, PublicRoute } from '@puglieseweb/shared-react-auth';
import { useAuthContext } from '@/contexts/AuthContext';
import { TenantNeutralGate } from '@/components/layout/TenantNeutralGate';
import LoginPage, { PostLoginRedirect } from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import MFASetupPage from '@/pages/auth/MFASetupPage';
import JoinPage from '@/pages/workspace/JoinPage';
import WelcomePage from '@/pages/workspace/WelcomePage';
import LaunchPage from '@/pages/LaunchPage';
import GitHubCallbackPage from '@/pages/settings/GitHubCallbackPage';

export function NeutralHostRoutes() {
  const { isAuthenticated, isLoading } = useAuthContext();

  return (
    <>
    <PostLoginRedirect />
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading} homePath="/launch">
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute isAuthenticated={isAuthenticated} isLoading={isLoading} homePath="/launch">
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route path="/join" element={<JoinPage />} />
      <Route
        path="/mfa-setup"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <MFASetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <WelcomePage />
          </ProtectedRoute>
        }
      />
      {/* GitHub App post-install redirect — see rule 1 in the header. */}
      <Route
        path="/settings/github/callback"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <GitHubCallbackPage />
          </ProtectedRoute>
        }
      />
      {/* Post-login: the TenantNeutralGate resolves the workspace and forwards
          to its root host ({tenant}.systemdox.com), where the whole app runs. */}
      <Route
        path="/launch"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <TenantNeutralGate>
              <LaunchPage />
            </TenantNeutralGate>
          </ProtectedRoute>
        }
      />
      {/* Any other path funnels through the gate, which forwards it — query
          and hash intact — to the tenant root host; the /launch rewrite is
          only the gate's own fallback. See rule 2 in the header. */}
      <Route
        path="/*"
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <TenantNeutralGate>
              <Navigate to="/launch" replace />
            </TenantNeutralGate>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}
